import { useFieldArray, useForm, useWatch } from "react-hook-form";
import useLabels from "../../hooks/useLabels";
import {
  DatePicker,
  Input,
  Label,
  MultipleSelect,
  Select,
  Submit,
  Switch,
  TextArea,
} from "../FormComponents";
import {
  addDays,
  addHours,
  addMinutes,
  differenceInHours,
  eachDayOfInterval,
  format,
  roundToNearestMinutes,
} from "date-fns";
import { useNavigate, useParams } from "react-router";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useDispatch } from "react-redux";
import { newOperation, updateOperation } from "../../services/utils";
import { add, updateOp } from "../../context/operations/operationsSlice";
import { useEffect, useRef, useState } from "react";
import SubstanceList from "../SubstanceList";
import {
  ConfirmationModal,
  LotOrTankOperationSelector,
  OperationRecap,
} from "../Generic";
import {
  ALPHA_NUM_SPACE_DASH_UNDERSCORE_REGEX,
  INT_REGEX,
} from "../../constants/regex";
import { toast } from "react-toastify";
import { buildTreatmentsDataArray } from "../../services/common";
import { toastAxios } from "../../services/toastAxios";

const AnalysisForm = ({
  type,
  operation,
  operations,
  protocols,
  allSubstances,
  activeWinery,
  tanks,
  users,
  configs,
  lots,
  isDuplicate = false,
}) => {
  const { id } = useParams();
  const [getLabel] = useLabels();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isExactDate, setIsExactDate] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  
  const [substancesWithQuantity, setSubstancesWithQuantity] = useState([]);
  const [filteredSubstances, setFilteredSubstances] = useState([]);
  const [totalTreatmentVolume, setTotalTreatmentVolume] = useState(0);
  const axiosPrivate = useAxiosPrivate();
  const isFirstLoadRef = useRef(true);

  const [srcTankEnabledFieldArray, setSrcTankEnabledFieldArray] = useState([]);

  useEffect(() => {
    if (!protocols || protocols.length === 0) {
      return;
      // loadProtocols();
    }
    if (id && (!operations || operations?.length === 0)) return; // loadOperations();
    if (id && operations?.length > 0) {
      const operation = operations?.find((o) => o.id === Number(id)) || null;
      if (operation === null) return;

      setValue("priority", {
        label: getLabel(`operationPriority${operation?.priority}`),
        value: operation?.priority,
      });

      const info = operation?.treatments?.[0]?.info || {};

      const srcs = [];
      const srcsBatch = [];
      operation?.src_tanks?.map((s) => {
        const tank = tanks?.find((t) => t.id === Number(s.tank_id));
        srcs.push({
          quantity: s?.quantity,
          tankData: {
            value: s?.tank_id,
            label: tank?.name,
            availability: Number(tank?.quantity),
            capacity: Number(tank?.capacity),
            quantity: Number(tank?.quantity),
            color: tank?.color,
            state: tank?.state,
            lot: tank?.batch_name || getLabel("tankState_EMPTY"),
            name: tank?.name + ` (${tank?.batch_name})`,
          },
        });
      });

      if (info?.analysisType === "lot") {
        const allTanksId = operation?.src_tanks?.map((s) => Number(s?.tank_id));
        for (const tank_id of allTanksId) {
          setValue(`edit.${tank_id}`, true);
        }
        const currentLots = lots?.filter((l) =>
          l?.tanks?.some((tt) => allTanksId?.includes(Number(tt?.tank_id)))
        );
        for (const currentLot of currentLots) {
          const tmp = {};
          for (const t of currentLot?.tanks) {
            tmp[t?.tank_id] = allTanksId?.includes(Number(t.tank_id));
          }
          srcsBatch.push({
            lotData: {
              ...tmp,
              value: currentLot?.id,
              label: currentLot?.name,
              isTank: true,
            },
          });
        }
        setSrcTankEnabledFieldArray(allTanksId);
        srcBatchFieldArray.replace(srcsBatch);
      }

      if (info?.analysisType === "tank") {
        srcTankFieldArray.replace(srcs);

        if (type === "TREATMENT") {
          const totalVolume = operation?.src_tanks?.reduce((acc, curr) => {
            return (
              acc + (Number(curr?.quantity) || 0)
            );
          }, 0) || 0;
          setTotalTreatmentVolume(totalVolume);
        }
      }
      
      const cellarmans = [];
      operation?.cellarman_ids?.map((u) => {
        const user = users?.find((us) => us.id === u);
        cellarmans.push({
          value: u,
          label: user?.username,
        });
      });
      setValue("cellarman_ids", cellarmans);

      setValue("custom_name", operation?.custom_name || "");

      // repetitive form data
      setValue("protocolFrequency", {
        label: getLabel(
          info?.protocolFrequency === "repeat"
            ? "protocolRepeat"
            : "protocolSpot"
        ),
        value: info?.protocolFrequency === "repeat" ? "repeat" : "spot",
      });

      setValue("analysisType", {
        label: getLabel(info?.analysisType),
        value: info?.analysisType,
      });

      if (Number(operation?.total) > 1 && operation?.repetitive) {
        setValue("repeatSize", {
          label: getLabel(`repeatSize_${info?.repeatSize}`),
          value: info?.repeatSize,
        });
        setValue("repeatEnd", {
          label: getLabel(`repeatEnd_${info?.repeatEnd}`),
          value: info?.repeatEnd,
        });
        setValue(
          "repeatDay",
          info?.repeatDay?.map((day) => {
            return {
              label: getLabel(`repeatDay_${info?.day}`),
              value: day,
            };
          })
        );

        setValue("repeat_start_date", new Date(info?.repeat_start_date));
        setValue("repeat_end_date", new Date(info?.repeat_end_date));
        setValue("repeatOccurrences", info?.repeatOccurrences);
        setValue("repeatEvery", info?.repeatEvery);
      }

      setValue("expire_date", new Date(operation?.expire_date));
      setValue("note", operation?.chat_notes?.[0]?.message || "");
      setIsExactDate(operation?.is_exact_date);
      const protocol = protocols?.find(
        (p) => p?.protocols_id === operation?.protocol_id
      );

      setValue("protocol_id", {
        value: operation?.protocol_id,
        label: protocol?.protocols_name,
        state: protocol?.state,
      });

      // buildProtocolsData();
    }
  }, [operation, tanks]);

  // const loadProtocols = async () => {
  //   const currentProtocols = await getProtocols(
  //     type?.toLowerCase(),
  //     axiosPrivate
  //   );
  //   dispatch(
  //     type?.toLowerCase() === "treatment"
  //       ? setTreatments(currentProtocols)
  //       : setAnalysis(currentProtocols)
  //   );
  // };

  const getFilteredSubstances = (substances = [], importDataState = null) => {
    if (type?.toLowerCase() === "treatment")
      return substances?.map((s) => {
        return { ...s, opType: "PROTOCOL" };
      });
    else if (type?.toLowerCase() === "analysis") {
      const tmp = [];

      // Grouping function
      const grouped = substances
        ?.filter((s) =>
          importDataState !== null ? s?.states?.includes(importDataState) : true
        )
        ?.reduce((acc, item) => {
          const key = item.substance_name || item.name;
          if (!acc[key]) {
            acc[key] = [];
          }
          acc[key].push(item);

          return acc;
        }, {});
      // console.log('grouped', grouped)
      // Convert the result to an array of grouped objects (optional)
      Object.keys(grouped)?.forEach((key) => {
        const items = grouped[key];
        const firstItem = items?.[0];
        const substance_name = getLabel(`substance_${key}`);

        tmp.push({
          name: key,
          substance_id: firstItem?.substance_id,
          substance_name: key,
          substance_name_lower: substance_name?.toLowerCase(),
          unit: firstItem?.unit,
          unit_id: firstItem?.unit_id,
          // unit: firstItem?.unit,
          id: firstItem?.id,
          value: firstItem?.id,
          label: substance_name,
          items: items,
          states: firstItem?.states,
          opType: "PROTOCOL",
        });
      });

      return tmp?.sort((a, b) =>
        a?.substance_name_lower > b?.substance_name_lower
          ? 1
          : b?.substance_name_lower > a?.substance_name_lower
          ? -1
          : 0
      );
    } else return [];
  };

  const buildProtocolsData = () => {
    if (!protocol_id?.value) return; // Exit early if protocol_id is null

    const chosenProtocol = protocols?.find(
      (p) => p.protocols_id === protocol_id?.value
    );
    if (!chosenProtocol) return; // Exit if protocol not found

    // Get substances based on type
    const substances =
      type === "ANALYSIS"
        ? chosenProtocol?.analysis
        : chosenProtocol?.drugs || [];

    const isUpdate =
      isFirstLoadRef.current &&
      id &&
      protocol_id?.value === operation?.protocol_id;

    const uniqueSubstanceIds = new Set(
      isUpdate ? [] : substances.map((item) => item.substance_id)
    );
    if (isUpdate) {
      operation?.treatments?.forEach((t) =>
        uniqueSubstanceIds.add(t?.substance_id)
      );
    }

    let substancesWithQuantityTmp = [];

    if (type === "TREATMENT") {
      allSubstances
        ?.filter((s) => uniqueSubstanceIds.has(s.id))
        ?.forEach((treatment, i) => {
          const treatmentId = treatment.substance_id || treatment.id;
          if (
            substancesWithQuantityTmp.some(
              (s) => s.substance_id === treatmentId
            )
          )
            return;
            
          const drug_batch_number = treatment.drug_batch_number || null;
          const additionalData =
            allSubstances?.find((s) => s.id === treatmentId) || {};
          const expire_date = drug_batch_number
            ? additionalData?.batches?.find(
                (b) => b.batch_number === drug_batch_number
              )?.expire_date || null
            : null;
          const protocolDrug = isUpdate
            ? operation?.treatments?.find((t) => t.substance_id === treatmentId)
            : substances?.find((s) => s.substance_id === treatmentId);

          substancesWithQuantityTmp.push({
            substance_id: treatmentId,
            substance_name: treatment.substance_name || treatment?.name,
            quantity: protocolDrug?.quantity ?? treatment.quantity ?? additionalData.quantity,
            unit_id: treatment.unit_id,
            min_range: treatment.min_range,
            max_range: treatment.max_range,
            unit: treatment.unit,
            opType: "TREATMENT",
            removed: false,
            recommended_quantity: additionalData
              ? additionalData?.recommended_quantity
              : treatment?.recommended_quantity,
            batches: additionalData.batches,
            ...(drug_batch_number && {
              drug_batch_number,
              drug_batch_cost: treatment.drug_batch_cost,
            }),
            ...(expire_date && { expire_date }),
          });

          // Set drug batch data
          if (drug_batch_number) {
            setValue(`drug_batch.${i}`, {
              label: `${drug_batch_number} (${format(
                new Date(expire_date || treatment.expire_date),
                configs.shortDateFormat
              )})`,
              value: drug_batch_number,
            });
          }
        });
    } else if (type === "ANALYSIS") {
      substancesWithQuantityTmp = allSubstances?.filter((s) =>
        uniqueSubstanceIds.has(s.id)
      );
    }

    // console.log('substancesWithQuantityTmp', substancesWithQuantityTmp)
    // Update the state with filtered substances
    setSubstancesWithQuantity(getFilteredSubstances(substancesWithQuantityTmp));
  };

  const {
    register,
    handleSubmit,
    trigger,
    formState: { errors, isValid },
    reset,
    setFocus,
    setError,
    setValue,
    getValues,
    clearErrors,
    control,
  } = useForm({
    defaultValues: {
      // priority: operation?.priority || "",
      // protocolFrequency: "",
      // expire_date: operation?.expire_date || null,
      // type: "ANALYSIS",
      protocolFrequency: {
          label: getLabel("protocolSpot"),
          value: "spot",
        },
      type: type,
      src_lots: operation?.src_lots || [
        { lotData: { value: null, label: null } },
      ],
      src_tanks: operation?.src_tanks || [
        { tankData: { value: null, label: null } },
      ],
      cellarman_ids: operation?.cellarman_ids || "",
      custom_name: "",
      recursive_dates: [],
      analysisType:{
        label: getLabel("tank"),
        value: "tank",
      },
      drug_batches: {},
      drug_batches_tmp: [{ value: null, label: null }],
    },
    mode: "onTouched",
  });

  const srcBatchFieldArray = useFieldArray({
    control,
    name: "src_lots",
  });

  const srcTankFieldArray = useFieldArray({
    control,
    name: "src_tanks",
  });

  const drugBatchFieldArray = useFieldArray({
    control,
    name: "drug_batches",
  });

  const drugBatchFieldArrayTmp = useFieldArray({
    control,
    name: "drug_batches_tmp",
  });

  const { protocol_id, src_lots, analysisType, src_tanks, drug_batches } = useWatch({
    control,
  });

  const recursiveDates = useWatch({
    control,
    name: "recursive_dates", // match the useFieldArray name
  });

  const previousProtocolIdRef = useRef();

  // useEffect(() => {
  //   const protocol = protocols?.find(
  //     (p) => p?.protocols_id === protocol_id?.value
  //   ) || null;
  //   console.log('protocol', protocol, 'protocol_id', protocol_id?.value)
  //   if (protocol_id?.value === null || protocol === null) return;

  //   const state = protocol?.state;
  //   const prevState = previousProtocolIdRef?.current ? protocols?.find(
  //     (p) => p?.protocols_id === previousProtocolIdRef.current
  //   )?.state : null;

  //   // in case the state changes
  //   if (state !== prevState) {
  //     // Reset both field arrays
  //     srcBatchFieldArray.replace([{lotData: {value: null, label: null}}]);
  //     srcTankFieldArray.replace([{tankData: {value: null, label: null}}]);
  //   }

  //   // update the ref with the current protocol_id
  //   if (protocol_id !== previousProtocolIdRef.current) {
  //     previousProtocolIdRef.current = protocol_id?.value;
  //   }

  //   const filteredSubs = getFilteredSubstances(allSubstances, state);
  //   setFilteredSubstances(filteredSubs);

  //   if (substancesWithQuantity?.length === 0 || (protocol_id?.value !== previousProtocolIdRef.current)) buildProtocolsData();
  // }, [protocol_id]);

  useEffect(() => {
    if (!protocol_id?.value) return; // Exit if protocol_id is null

    const protocol =
      protocols?.find((p) => p?.protocols_id === protocol_id?.value) || null;

    if (!protocol) return; // Exit if protocol is not found

    const state = protocol?.state;
    const prevState = previousProtocolIdRef.current
      ? protocols?.find(
          (p) => p?.protocols_id === previousProtocolIdRef.current
        )?.state
      : null;

    // Reset src_lots and src_tanks if the protocol state changes
    if (!isFirstLoadRef.current && state !== prevState) {
      srcBatchFieldArray.replace([{ lotData: { value: null, label: null } }]);
      srcTankFieldArray.replace([{ tankData: { value: null, label: null } }]);
    }

    // Update substances based on the new protocol
    setFilteredSubstances(getFilteredSubstances(allSubstances, state));

    // Update protocol-related substances
    if (protocol_id?.value !== previousProtocolIdRef.current) {
      buildProtocolsData();
    }

    // Update reference for protocol_id
    previousProtocolIdRef.current = protocol_id?.value;

    // Mark that the first load has completed so the editing of an existing op does not reset the fields
    isFirstLoadRef.current = false;
  }, [protocol_id, protocols]); // Only re-run when protocol_id or protocols change

  // const columns = useMemo(() => [
  //   {
  //     Header: getLabel("tankName"),
  //     accessor: "name",
  //   },
  //   {
  //     Header: getLabel("quantity") + (type === "TREATMENT" ? " (L)" : ""),
  //     accessor: "quantity",
  //   },
  //   {
  //     Header: getLabel("tankSelected"),
  //     accessor: (row) => (
  //       <Input
  //         name={`src_lots.${row.idx}.lotData.${row.id}`}
  //         register={register}
  //         label={getLabel(`operationSrcTankQuantity`)}
  //         placeholder={getLabel("operationSrcTanksQuantityPlaceholder")}
  //         error={errors[`src_tanks.${row.id}`]}
  //         //   required={getLabel("operationSrcTankQuantityRequiredError")}
  //         type={"checkbox"}
  //         isLabel={false}
  //         checked={row?.enabled}
  //         onChange={(e) => toggleTankEnabled([row.id], e.target.checked)}
  //         onKeyDown={(e) => {
  //           if (e.key === "Enter") {
  //             e.preventDefault(); // Prevent default Enter behavior (e.g., form submission)
  //             toggleTankEnabled([row.id], e.target.checked); // Change the checkbox state
  //             setValue(`src_lots.${row.idx}.lotData.${row.id}`, e.target.checked);
  //           }
  //         }}
  //       />
  //     ),
  //   },
  // ]);

  // const toggleTankEnabled = (tank_ids, checked) => {
  //   let tmpArray = srcTankEnabledFieldArray;
  //   tank_ids.forEach((tank_id) => {
  //     const found = tmpArray.includes(tank_id);
  //     if (!found && checked) {
  //       tmpArray.push(tank_id);
  //     } else if (found && !checked) {
  //       const foundIndex = tmpArray.indexOf(tank_id);
  //       if (foundIndex > -1) tmpArray = tmpArray.filter((t) => t != tank_id);
  //     }
  //   });

  //   setSrcTankEnabledFieldArray(tmpArray);
  // };


  const customIdMissingDests = "custom-id-missing-dests";
  const customIdInvalidState = "custom-id-invalid-state";

  const roundMinutes = (date) => {
    date.setHours(date.getHours() + Math.round(date.getMinutes() / 60));
    date.setMinutes(0, 0, 0); // Resets also seconds and milliseconds

    return date;
  };

  const sameDay = (d1, d2) => {
    return (
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate()
    );
  };

  const getDayIndex = (value) => {
    switch (value) {
      case "MONDAY":
        return 1;
      case "TUESDAY":
        return 2;
      case "WEDNESDAY":
        return 3;
      case "THURSDAY":
        return 4;
      case "FRIDAY":
        return 5;
      case "SATURDAY":
        return 6;
      case "SUNDAY":
        return 7;
      default:
        return 0;
    }
  };

  const handleCloseModal = () => {
    setIsOpen(false);
  };

  const getHoursToAdd = (startDate, repeatEvery) => {
    const dailyEndDate = new Date(startDate)?.setHours(19, 0, 0);
    const hoursDiff = dailyEndDate.valueOf() - startDate.valueOf();
    if (hoursDiff < 0) return;
    const diffInHours = hoursDiff / 1000 / 60 / 60;
    const hoursToAdd = Math.round((diffInHours * 100) / repeatEvery) / 100;

    return hoursToAdd;
  };

  const openFormModal = () => {
    const tmpDates = [];

    const {
      repeatEvery,
      repeatSize,
      repeatDay,
      repeatEnd,
      repeat_end_date,
      repeat_start_date,
      repeatOccurrences,
    } = getValues();

    const scaleFactor =
      repeatSize?.value === "HOUR"
        ? 1
        : repeatSize?.value === "DAILY"
        ? 1
        : repeatSize?.value === "DAY"
        ? 24
        : repeatSize?.value === "WEEK"
        ? 24 * 7
        : repeatSize?.value === "MONTH"
        ? 24 * 7 * 4
        : null;

    if (scaleFactor === null) return;

    const startDate = addHours(
      sameDay(repeat_start_date, new Date())
        ? new Date()
        : new Date(repeat_start_date)?.setHours(7, 0, 0),
      repeatSize?.value === "DAILY" ? 0 : repeatEvery * scaleFactor
    );
    const endDate =
      repeatEnd?.value === "DATE"
        ? addMinutes(
            new Date(repeat_end_date)?.setHours(23, 59, 59),
            -new Date(repeat_end_date).getTimezoneOffset()
          )
        : addDays(new Date(), 365);

    let i = 1;

    if (!["WEEK", "DAILY"].includes(repeatSize?.value)) {
      for (
        let newDate = startDate;
        newDate < endDate;
        newDate = addHours(newDate, repeatEvery * scaleFactor)
      ) {
        if (
          repeatSize?.value === "HOUR" &&
          (newDate.getHours() <= 7 || newDate.getHours() >= 19)
        )
          continue;
        tmpDates.push(
          repeatSize?.value === "HOUR"
            ? roundMinutes(newDate)
            : new Date(newDate.setHours(23, 59, 59))
        );
        i++;
        if (
          i > 100 ||
          tmpDates?.length >= 15 ||
          (repeatOccurrences && tmpDates?.length >= repeatOccurrences)
        )
          break;
      }
    } else if (repeatSize?.value === "DAILY") {
      // if isSameDayStart, the hoursToAdd is lower because some hours have already passed, so recompute it for the next day
      const isSameDayStart = sameDay(startDate, new Date());
      const hoursToAdd = getHoursToAdd(
        new Date(repeat_start_date)?.setHours(7, 0, 0),
        repeatEvery
      );
      const hoursToAddSameDay = getHoursToAdd(startDate, repeatEvery);

      const fakeDate =
        repeatEnd?.value === "OCCURRENCES" ? addDays(new Date(), 365) : null; // trick: fake date to have 'repeatOccurrences'
      const daysInterval = sameDay(startDate, new Date(repeat_end_date))
        ? [startDate]
        : eachDayOfInterval({
            start: new Date(startDate),
            end:
              repeatEnd?.value === "OCCURRENCES"
                ? fakeDate
                : new Date(repeat_end_date),
          });

      for (let i = 0; i < daysInterval?.length; i++) {
        let newDate = new Date(daysInterval?.[i]);
        const hours = isSameDayStart && i === 0 ? startDate.getHours() : 7;
        newDate.setHours(hours);
        let diffInHours = 0;
        if (isSameDayStart && i === 0) {
          const todayEnd = new Date(startDate);
          todayEnd.setHours(19);
          diffInHours = differenceInHours(todayEnd, startDate);
        }

        for (let j = 1; j <= repeatEvery; j++) {
          const newHours =
            hours +
            (isSameDayStart && i === 0 ? hoursToAddSameDay : hoursToAdd) * j;
          newDate.setHours(newHours);
          if (isSameDayStart && i === 0 && diffInHours > 0) {
            const minutes = newHours - Math.floor(newHours);
            newDate.setMinutes(minutes * 60);
            newDate = roundToNearestMinutes(newDate, {
              roundingMethod: "ceil",
              nearestTo: 10,
            });
          }
          if (
            repeatEnd?.value === "DATE" ||
            (repeatEnd?.value === "OCCURRENCES" &&
              Number(repeatOccurrences) > 0 &&
              tmpDates?.length < Number(repeatOccurrences))
          )
            tmpDates.push(new Date(newDate));
          // tmpDates.push(roundMinutes(newDate));
        }
      }
    } else {
      // WEEK: compute each selected weekday independently per cycle so the
      // result does NOT depend on the order the days were selected, and never
      // produces dates before the start date (no dates in the past).
      // Our day index: MONDAY=1 ... SUNDAY=7. JS getDay(): SUNDAY=0 ... SATURDAY=6.
      const toIndex = (date) => (date.getDay() === 0 ? 7 : date.getDay());

      const selectedDays = (repeatDay || [])
        ?.map((el) => getDayIndex(el?.value))
        ?.filter((idx) => idx >= 1 && idx <= 7)
        ?.sort((a, b) => a - b);

      if (!selectedDays || selectedDays.length === 0) return;

      // Floor: nothing before the start date (start day itself is allowed).
      const start = new Date(repeat_start_date);
      start.setHours(0, 0, 0, 0);

      // Anchor on the Monday of the start week, then advance whole cycles of
      // (repeatEvery) weeks; weekday alignment is preserved.
      let weekStart = new Date(start);
      weekStart.setDate(weekStart.getDate() - (toIndex(weekStart) - 1));

      outer: while (i < 100 && weekStart < endDate) {
        for (const dayIdx of selectedDays) {
          const d = new Date(weekStart);
          d.setDate(weekStart.getDate() + (dayIdx - 1));
          d.setHours(23, 59, 59, 0);

          if (d < start) continue; // skip days earlier than the start date
          if (repeatEnd?.value === "DATE" && d > endDate) break outer;

          tmpDates.push(d);
          i++;

          if (
            tmpDates?.length >= 15 ||
            (repeatOccurrences && tmpDates?.length >= Number(repeatOccurrences))
          )
            break outer;
        }
        weekStart = addDays(weekStart, 7 * repeatEvery);
      }
    }
    // console.log(tmpDates);
    const isTime = ["HOUR", "DAILY"].includes(repeatSize?.value);

    const dates = tmpDates
      ?.sort(function (d1, d2) {
        return d1 - d2;
      })
      ?.map((d) => {
        return {
          date: d,
          checked: true,
          format: isTime ? configs.dateFormat : configs.mediumDateFormat,
        };
      });

    // setRecursiveDates(dates);
    setValue("recursive_dates", dates);

    // if (true === 0) {
    //   toast(
    //     getLabel("toast_destemmingMissingDests"),
    //     {
    //       toastId: customIdMissingDests,
    //       type: toast.TYPE.ERROR,
    //       isLoading: false,
    //       position: toast.POSITION.BOTTOM_RIGHT,
    //     }
    //   );
    //   return;
    // } else {
    //   toast.dismiss(customIdMissingDests);
    // }

    // if (true) {
    //   toast(
    //     getLabel("toast_destemmingMissingLotSelectors"),
    //     {
    //       toastId: customIdMissingDests,
    //       type: toast.TYPE.ERROR,
    //       isLoading: false,
    //       position: toast.POSITION.BOTTOM_RIGHT,
    //     }
    //   );
    //   return;
    // } else {
    //   toast.dismiss(customIdMissingDests);
    // }

    setIsOpen(true);

    // const totalLitersAvailable = Number(getValues("tank_id_in")?.quantity);
    // if (0 <= totalLitersAvailable) {
    //   toast.dismiss(customIdInvalidQuantities);
    //   setIsOpen(true);
    // } else {
    //   toast(
    //     getLabel("toast_transferInvalidQuantities", {
    //       sum: 0 || "0",
    //       total: totalLitersAvailable,
    //     }),
    //     {
    //       toastId: customIdInvalidQuantities,
    //       type: toast.TYPE.ERROR,
    //       isLoading: false,
    //       position: toast.POSITION.BOTTOM_RIGHT,
    //     }
    //   );
    //   return;
    // }
  };


  const customIdDrugBatches = "custom-id-drug-batches";
  const customIdNewOp = "custom-id-new-op";
  const customIdUpdateOp = "custom-id-update-op";
  
  const submitForm = async (d) => {
    const data = d ? d : getValues();

    const src_tank_ids =
      analysisType.value === "tank"
        ? data?.src_tanks?.map((s) => s?.tankData?.id || s?.tankData?.value)
        : (d?.edit || [])
            ?.map((value, index) => (value === true ? index : -1))
            ?.filter((index) => index !== -1)
            ?.filter((item) => item !== null);

    const expire_dates =
      data?.protocolFrequency?.value !== "repeat"
        ? []
        : recursiveDates
            ?.filter((d) => d?.checked)
            ?.map((d) =>
              addMinutes(
                new Date(d?.date),
                -new Date(d?.date).getTimezoneOffset()
              ).toISOString()
            );

    if (
      expire_dates?.length === 0 &&
      new Date(data?.expire_date?.setHours(23, 59, 59)) < new Date()
    ) {
      setError("expire_date", {
        type: "manual",
        message: getLabel("errorDateInThePast"),
      });
      return;
    }

    const hasGrape = src_lots.some((lot) => lot.lotData?.state === "GRAPE");
    const allGrape = src_lots.every((lot) => lot.lotData?.state === "GRAPE");
    const isGrapeAnalysis = hasGrape && allGrape;

    if (
      !isGrapeAnalysis &&
      // analysisType.value === "lot" &&
      (!Array.isArray(src_tank_ids) || src_tank_ids?.length === 0)
      //  ||
      // !data?.src_lots?.every(
      //   (lot) => lot?.lotData?.value && lot?.lotData?.label
      // ))
    ) {
      toast(getLabel("toast_destemmingMissingDestsLots"), {
        toastId: customIdMissingDests,
        type: toast.TYPE.ERROR,
        isLoading: false,
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      return;
    } else {
      toast.dismiss(customIdMissingDests);
    }

    // add form info when repetitive
    const info =
      data?.protocolFrequency?.value === "repeat"
        ? {
            repeat_start_date: data?.repeat_start_date,
            repeat_end_date: data?.repeat_end_date,
            repeatDay: data?.repeatDay?.map((d) => d?.value),
            repeatEvery: data?.repeatEvery,
            repeatSize: data?.repeatSize?.value,
            repeatEnd: data?.repeatEnd?.value,
            repeatOccurrences: data?.repeatOccurrences,
          }
        : {};
    info.analysisType = data?.analysisType?.value;
    info.protocolFrequency = data?.protocolFrequency?.value;
    info.isGrapeAnalysis = isGrapeAnalysis;

    const src_tanks = !isGrapeAnalysis
      ? src_tank_ids?.map((t) => {
          return { tank_id: t };
        })
      : src_lots?.map((b) => {
          return { batch_id: b?.lotData?.value };
        });
    src_tanks[0] = { ...src_tanks[0], info: info };
    
    for (let i = 0; i < src_tank_ids?.length; i++) {
      const t = tanks?.find((tt) => tt?.id == src_tank_ids[i]);
      if (Number(t?.quantity) <= 0 && data?.analysisType?.value === "tank") {
        setError(`src_tanks.${i}.tankData`, {
          type: "manual",
          message: getLabel("error0QuantityNewModify"),
        });
        return;
      }
    }

  
    const requestPayload = {
      src_tanks: src_tanks,
      priority: data?.priority?.value,
      // ...(expire_dates?.length === 0 && { expire_date: addMinutes(
      //   new Date(data?.expire_date),
      //   -new Date(data?.expire_date).getTimezoneOffset()
      // ).toISOString() }),
      is_exact_date: expire_dates?.length === 0 ? isExactDate : false,
      ...(expire_dates?.length === 0 && {
        expire_date: new Date(
          data?.expire_date?.setHours(23, 59, 59)
        ).toISOString(),
      }),
      ...(expire_dates?.length > 0 && { expire_dates: expire_dates }),
      type: type,
      subtype: data?.analysisType?.value || "tank",
      winery_id: activeWinery?.id,
      protocol_id: protocol_id?.value,
      cellarman_ids: data?.cellarman_ids?.map((cellMan) => cellMan?.value),
      note: data?.note?.trim(),
      custom_name: data?.custom_name?.trim(),
    };
    
    const treatments_data = buildTreatmentsDataArray(allSubstances, drug_batches, data, protocols, protocol_id, type, id, operation, operations, analysisType);
    
    requestPayload.treatments_data = treatments_data;
    
    console.log("requestPayload", requestPayload);
    // return;
    let response = null;

    if (id && !isDuplicate) {
      response = await toastAxios(
        () => updateOperation(id, requestPayload, axiosPrivate),
        customIdUpdateOp,
        { name: operation?.type || "" },
        getLabel,
      );
    } else {
      response = await toastAxios(
        () => newOperation(requestPayload, axiosPrivate),
        customIdNewOp,
        { name: operation?.type || "" },
        getLabel,
      );
    }
    console.log("response", response);

    if (response && response?.success) {
      const updated_op = response?.updated_data || null;
      handleCloseModal();
      updateStore(updated_op);
      // dispatch(empty());
      navigate("/programming");
    } else {
      handleCloseModal();
    }
  };

  const updateStore = (payload) => {
    if (!payload) return;
    const operations = Array.isArray(payload) ? payload : [payload];
    operations.forEach(op => {
      if (id && !isDuplicate) {
        dispatch(updateOp(op));
      } else {
        dispatch(add(op));
      }
    });
  };

  const checkState = (idx, validSubstanceIds = []) => {
    try {
      // const new_tank = ((analysisType.value === "lot") ? getValues(`src_lots.${idx}.lotData`) : getValues(`src_tanks.${idx}.tankData`)) || null;
      const new_tank = tanks?.find((t) => t?.id == idx) || null;

      if (!new_tank || !new_tank?.state) {
        console.error("Error retrieving tank data for id:", idx);
        return false;
      }
      if (type === "ANALYSIS") {
        let valid = true;
        for (const s of allSubstances?.filter((s) =>
          validSubstanceIds.includes(s?.id)
        )) {
          // for (const s of substancesWithQuantity?.filter(s => validSubstanceIds.includes(s?.substance_id))) {
          const allowedStates = s?.states || null;
          // console.log('new_tank', new_tank, allowedStates)
          if (allowedStates === null || allowedStates?.length === 0) {
            console.error("Error retrieving substance's states");
          }
          if (!allowedStates?.includes(new_tank?.state)) {
            const allowedStatesString = allowedStates?.reduce(
              (accumulator, current) => {
                return (
                  accumulator +
                  (accumulator ? ", " : "") +
                  getLabel(`lotState_${current}`)
                );
              },
              ""
            );
            toast.error(
              getLabel("toast_analysisProtocolInvalidTankState", {
                name: getLabel(`substance_${s?.name}`),
                tank_name: new_tank?.name,
                states: allowedStatesString?.toLowerCase(),
                state: getLabel(`lotState_${new_tank?.state}`)?.toLowerCase(),
              }),
              {
                toastId: customIdInvalidState,
                isLoading: false,
                position: toast.POSITION.BOTTOM_RIGHT,
              }
            );
            valid &= false;
          }
        }

        return valid;
      }
    } catch (err) {
      console.error(err.message);
      return false;
    }
  };

  return (
    <div className="operation_form_wrapper">
      <form
        onSubmit={handleSubmit(
          getValues("protocolFrequency")?.value === "repeat"
            ? openFormModal
            : submitForm,
          (errors) => console.log("Form validation errors:", errors)
        )}
        autoComplete="off"
        noValidate
      >
        <h4>
          {type === "ANALYSIS"
            ? getLabel("analysisConfiguration")
            : getLabel("treatmentsConfiguration")}
        </h4>
        <div>
          <div className="operation_form_wrapper_row">
            <Select
              name="protocol_id"
              label={getLabel("protocolName")}
              placeholder={getLabel("protocolApplicationNamePlaceholder")}
              control={control}
              error={errors}
              required={getLabel("inputRequiredError")}
              options={protocols?.map?.((p) => {
                return {
                  value: p?.protocols_id,
                  label: p?.protocols_name,
                  state: p?.state,
                  protocol_subtype: p?.protocol_subtype,
                  stabilization_ph: p?.stabilization_ph,
                  work_time: p?.work_time,
                  operations: p?.operations,
                };
              })}
              onTableChange={() => {
                const hasGrape = src_lots.some(
                  (lot) => lot.lotData?.state === "GRAPE"
                );
                if (type === "ANALYSIS" && hasGrape && false)
                  setValue("analysisType", {
                    label: getLabel("lot"),
                    value: "lot",
                  });
                setValue("column", {});
                setValue("removed", []);
                setValue("quantities", []);
                // buildProtocolsData();
              }}
            />
            
            {protocol_id?.value && type === "ANALYSIS" && (
              <div className="protocol_info_container_row">
                <Label label={getLabel("analysisState")}>
                  {protocol_id?.state
                    ? getLabel(`lotState_${protocol_id?.state}`)
                    : ""}
                </Label>
              </div>
            )}
          </div>
          <div className="operation_form_wrapper_row">
            {protocol_id?.value && (
              <>
                <SubstanceList
                  isProtocolApplication={true}
                  register={register}
                  errors={errors}
                  control={control}
                  substanceType={
                    type?.toLowerCase?.() === "treatment"
                      ? "substance"
                      : "analysis"
                  }
                  substances={substancesWithQuantity}
                  // allSubstances={applyFilter()}
                  allSubstances={filteredSubstances}
                  isSearchBar={false}
                  reset={reset}
                  clearErrors={clearErrors}
                  setValue={setValue}
                  getValues={getValues}
                  isCheckbox={true}
                  disabledIds={operation?.treatments || []}
                  automaticDrugBatches={drugBatchFieldArray}
                  drugBatches={drugBatchFieldArrayTmp}
                  totalTreatmentVolume={totalTreatmentVolume}
                  operation_id={operation?.id}
                />
              </>
            )}
          </div>
        </div>
        <LotOrTankOperationSelector
          type={type}
          analysisType={analysisType}
          control={control}
          errors={errors}
          protocol_id={protocol_id}
          lots={lots}
          tanks={tanks}
          src_lots={src_lots}
          src_tanks={src_tanks}
          srcBatchFieldArray={srcBatchFieldArray}
          srcTankFieldArray={srcTankFieldArray}
          srcTankEnabledFieldArray={srcTankEnabledFieldArray}
          setSrcTankEnabledFieldArray={setSrcTankEnabledFieldArray}
          getValues={getValues}
          setValue={setValue}
          getLabel={getLabel}
          register={register}
          setTotalTreatmentVolume={setTotalTreatmentVolume}
        />
        {type === "ANALYSIS" && (
          <>
            <h5>{getLabel("operationAdditionalConfiguration")}</h5>
            <div className="operation_form_wrapper_row">
              <Input
                name="custom_name"
                register={register}
                type="text"
                label={getLabel("operationAnalysisName")}
                placeholder={getLabel("operationAnalysisNamePlaceholder")}
                minLength={{
                  value: 3,
                  message: getLabel("errorMinLength", { value: 3 }),
                }}
                maxLength={{
                  value: 24,
                  message: getLabel("errorMaxLength", { value: 24 }),
                }}
                regExpPattern={{
                  value: ALPHA_NUM_SPACE_DASH_UNDERSCORE_REGEX,
                  message: getLabel("errorAlphaNumSpace_-"),
                }}
                error={errors["custom_name"]}
                tooltip={getLabel("operationAnalysisNameTooltip")}
                validate={(value) => {
                  const val = value?.trim() || null;
                  if (!val) return;
                  const analysisOps =
                    operations?.filter(
                      (o) => o?.type === "ANALYSIS" || o?.type === "TREATMENT"
                    ) || [];
                  const isNameAlreadyUsed =
                    analysisOps?.some((o) => {
                      return (
                        o?.name?.toLowerCase() === val?.toLowerCase() &&
                        o?.id !== id
                      );
                    }) || null;
                  if (isNameAlreadyUsed)
                    return getLabel("errorAnalysisNameUsed", { value: val });
                }}
              />
            </div>
          </>
        )}
        <div className="operation_form_wrapper_row">
          <MultipleSelect
            name={`cellarman_ids`}
            control={control}
            label={getLabel("operationUser")}
            placeholder={getLabel("operationUserPlaceholder")}
            error={errors["cellarman_ids"]}
            required={getLabel("inputRequiredError")}
            options={users?.map?.((user) => {
              return {
                value: user?.id,
                label: user?.username,
              };
            })}
            tooltip={getLabel("destemmingCellarmenTooltip")}
          />
          <Select
            name="priority"
            control={control}
            label={getLabel("operationPriority")}
            placeholder={getLabel("operationPriorityPlaceholder")}
            error={errors}
            required={getLabel("inputRequiredError")}
            options={[
              {
                label: getLabel("lotQuality_HIGH"),
                value: "2",
              },
              {
                label: getLabel("lotQuality_MEDIUM"),
                value: "1",
              },
              {
                label: getLabel("lotQuality_LOW"),
                value: "0",
              },
            ]}
            // defaultValue={{
            //     value: "",
            //     label: "",
            //   }}
          />
          <Select
            label={getLabel("protocolFrequency")}
            placeholder={getLabel("protocolFrequencyPlaceholder")}
            error={errors}
            name="protocolFrequency"
            control={control}
            required={getLabel("inputRequiredError")}
            options={[
              {
                label: getLabel("protocolSpot"),
                value: "spot",
              },
              {
                label: getLabel("protocolRepeat"),
                value: "repeat",
              },
            ]}
          />
          {getValues("protocolFrequency")?.value === "spot" && (
            <>
              <DatePicker
                name="expire_date"
                control={control}
                label={getLabel("operationExpireDateForm")}
                error={errors["expire_date"]}
                required={getLabel("inputRequiredError")}
                dateFormat={configs.shortDateFormat}
                placeholder={getLabel("expiringDatePlaceholder")}
              />
              {getValues("expire_date") && (
                <Input
                  name={"expire_date_exact"}
                  register={register}
                  label={getLabel("isExactDate")}
                  error={errors["expire_date_exact"]}
                  // isLabel={false}
                  type={"checkbox"}
                  checked={isExactDate}
                  onChange={() => setIsExactDate((prev) => !prev)}
                  customClassName={"confirm no_margin_top"}
                  tooltip={getLabel("destemmingExactDate")}
                />
              )}
            </>
          )}
        </div>
        {getValues("protocolFrequency")?.value === "repeat" && (
          <div className="operation_form_wrapper_row">
            <DatePicker
              name="repeat_start_date"
              control={control}
              label={getLabel("operationStartDateForm")}
              error={errors["repeat_start_date"]}
              required={getLabel("inputRequiredError")}
              dateFormat={configs.shortDateFormat}
              placeholder={getLabel("expiringDatePlaceholder")}
              defaultValue={new Date()}
            />
            <Input
              name={"repeatEvery"}
              register={register}
              type="number"
              customClassName="quantity"
              error={errors["repeatEvery"]}
              label={getLabel(`repeatEvery`)}
              placeholder={getLabel("repeatEveryPlaceholder")}
              tooltip={getLabel("repeatEveryTooltip")}
              required={getLabel("inputRequiredError")}
              max={{
                value: 100,
                message: getLabel("errorMaxValue", { value: 100 }),
              }}
              min={{
                value: 1,
                message: getLabel("errorMinValue", { value: 1 }),
              }}
              regExpPattern={{
                value: INT_REGEX,
                message: getLabel("errorInt"),
              }}
            />
            <Select
              name="repeatSize"
              control={control}
              label={getLabel("repeatSize")}
              placeholder={getLabel("repeatSizePlaceholder")}
              error={errors["repeatSize"]}
              required={getLabel("inputRequiredError")}
              options={[
                {
                  label: getLabel("repeatSize_DAILY"),
                  value: "DAILY",
                },
                // {
                //   label: getLabel("repeatSize_HOUR"),
                //   value: "HOUR",
                // },
                {
                  label: getLabel("repeatSize_DAY"),
                  value: "DAY",
                },
                {
                  label: getLabel("repeatSize_WEEK"),
                  value: "WEEK",
                },
                // {
                //   label: getLabel("repeatSize_MONTH"),
                //   value: "MONTH",
                // },
              ]}
              // tooltip={getLabel("repeatSizeTooltip")}
              tooltip={getLabel("repeatSizeDailyTooltip")}
            />
            {getValues("repeatSize")?.value === "WEEK" && (
              <MultipleSelect
                name={`repeatDay`}
                control={control}
                label={getLabel("repeatDay")}
                placeholder={getLabel("repeatDayPlaceholder")}
                error={errors["repeatDay"]}
                required={getLabel("inputRequiredError")}
                options={[
                  {
                    label: getLabel("repeatDay_MONDAY"),
                    value: "MONDAY",
                  },
                  {
                    label: getLabel("repeatDay_TUESDAY"),
                    value: "TUESDAY",
                  },
                  {
                    label: getLabel("repeatDay_WEDNESDAY"),
                    value: "WEDNESDAY",
                  },
                  {
                    label: getLabel("repeatDay_THURSDAY"),
                    value: "THURSDAY",
                  },
                  {
                    label: getLabel("repeatDay_FRIDAY"),
                    value: "FRIDAY",
                  },
                  {
                    label: getLabel("repeatDay_SATURDAY"),
                    value: "SATURDAY",
                  },
                  {
                    label: getLabel("repeatDay_SUNDAY"),
                    value: "SUNDAY",
                  },
                ]}
                // tooltip={getLabel("destemmingCellarmenTooltip")}
              />
            )}
            <Select
              name="repeatEnd"
              control={control}
              label={getLabel("repeatEnd")}
              placeholder={getLabel("repeatEndPlaceholder")}
              error={errors["repeatEnd"]}
              required={getLabel("inputRequiredError")}
              options={[
                {
                  label: getLabel("repeatEnd_DATE"),
                  value: "DATE",
                },
                {
                  label: getLabel("repeatEnd_OCCURRENCES"),
                  value: "OCCURRENCES",
                },
              ]}
            />
            {getValues("repeatEnd")?.value === "DATE" && (
              <DatePicker
                name="repeat_end_date"
                control={control}
                label={getLabel("operationEndDateForm")}
                error={errors["repeat_end_date"]}
                required={getLabel("inputRequiredError")}
                dateFormat={configs.shortDateFormat}
                placeholder={getLabel("expiringDatePlaceholder")}
              />
            )}
            {getValues("repeatEnd")?.value === "OCCURRENCES" && (
              <Input
                name={"repeatOccurrences"}
                error={errors["repeatOccurrences"]}
                register={register}
                type="number"
                label={getLabel(`repeatOccurrences`)}
                placeholder={getLabel("repeatOccurrencesPlaceholder")}
                required={getLabel("inputRequiredError")}
                customClassName="quantity"
                max={{
                  value: 10,
                  message: getLabel("errorMaxValue", { value: 10 }),
                }}
                min={{
                  value: 1,
                  message: getLabel("errorMinValue", { value: 1 }),
                }}
                regExpPattern={{
                  value: INT_REGEX,
                  message: getLabel("errorInt"),
                }}
              />
            )}
          </div>
        )}
        <div className="client_form_wrapper_row">
          <TextArea
            name="note"
            register={register}
            label={getLabel("clientNotes")}
            placeholder={getLabel("clientNotesPlaceholder")}
            error={errors["note"]}
          />
        </div>
        <div className="operation_form_wrapper_row">
          <Submit label={getLabel("submitForm")}></Submit>
        </div>
      </form>
      {type === "ANALYSIS" && <ConfirmationModal
        isOpen={isOpen}
        onConfirm={submitForm}
        onClose={handleCloseModal}
        description={getLabel("modalNewRepetitiveAnalysis")}
        isOperationRecap={true}
        totalStep={0}
        errors={errors}
        getValues={getValues}
        // configs={configs}
      >
        <OperationRecap
          type={"RECURRENT_ANALYSIS"}
          control={control}
          register={register}
          getValues={getValues}
          errors={errors}
          configs={configs}
        />
      </ConfirmationModal>}
    </div>
  );
};
export default AnalysisForm;
